import { router } from "@inertiajs/react";
import axios from "axios";
import React, { useContext, useState } from "react";

import FormModal from "@/components/Modal/FormModal";
import ReferenceMonthPicker from "@/components/ReferenceMonthPicker";
import { AlertContext } from "@/contexts/Alert";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  consumer: any;
};

const currentReferenceMonth = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
};

export default function ToggleConsumerActiveModal({ isOpen, onClose, consumer }: Props) {
  const { showAlert } = useContext<any>(AlertContext);
  const deactivating = consumer?.active !== false;
  const [referenceMonth, setReferenceMonth] = useState(currentReferenceMonth);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const submit = () => {
    setSubmitting(true);
    setError(null);

    const request = deactivating
      ? axios.patch(`/manager/consumers/${consumer.user_id}/toggle_active`, {
          reference_month: referenceMonth,
        })
      : axios.patch(`/manager/consumers/${consumer.user_id}/toggle_active`);

    request
      .then(() => {
        showAlert({
          message: deactivating
            ? `Cliente ${consumer.name} desativado com sucesso.`
            : `Cliente ${consumer.name} ativado com sucesso.`,
          type: "success",
        });
        router.reload({ only: ["consumers", "consumersInfo"] });
        onClose();
      })
      .catch((e) => {
        const message = e?.response?.data?.error;
        setError(
          message || "Erro ao atualizar o status do cliente. Tente novamente ou contate o suporte.",
        );
      })
      .finally(() => setSubmitting(false));
  };

  return (
    <FormModal
      isOpen={isOpen}
      onClose={onClose}
      title={deactivating ? "Desativar cliente" : "Ativar cliente"}
      description={
        deactivating
          ? `Desativar o cliente ${consumer?.name}. Ele sairá das listagens e não receberá novas faturas.`
          : `Reativar o cliente ${consumer?.name}.`
      }
      submitButtonTitle={deactivating ? "Desativar" : "Ativar"}
      onSubmit={submit}
      isSubmitting={submitting}
      size="sm"
    >
      {deactivating && (
        <div className="grid grid-cols-1 gap-4">
          <div>
            <span className="mb-1 block text-xs font-bold uppercase text-grey500">
              Último mês ativo (mês de referência)
            </span>
            <ReferenceMonthPicker
              value={referenceMonth}
              onChange={setReferenceMonth}
              max={currentReferenceMonth()}
            />
            {error && <p className="mt-1 text-sm text-red-700">{error}</p>}
          </div>
          <p className="text-sm text-grey500">
            O mês de referência é o último mês em que o cliente esteve ativo: ele continua
            aparecendo nos relatórios e listagens desse mês (faturas, energia injetada, contas) e
            sai a partir do mês seguinte. Isso permite desativar retroativamente um cliente que saiu
            em um mês passado sem apagar o histórico — por exemplo, um cliente que saiu em março
            continua no relatório de março, mas some do de abril em diante.
          </p>
        </div>
      )}
    </FormModal>
  );
}
